import { Button } from '@avangard/ui/core';

import { CommonHelper } from '@helpers';
import testPlaceholderFailPath from '@modules/testing/helpers/assets/test-fail.svg';
import testPlaceholderSuccessPath from '@modules/testing/helpers/assets/test-completed.svg';

import type { CourseEntity } from '@modules/courses/entities';
import type { TestEntity } from '@modules/testing/entities';

export type TestingCoursePlaceholderPayload = {
    test: TestEntity;
    course: CourseEntity;
    t: (key: string, options?: Record<string, any>) => string;
    isActiveTest?: boolean;
    onStart?: () => void;
};

export type TestingCoursePlaceholderDataProps = {
    type: 'default' | 'success' | 'successNoCertificate' | 'failure';
    payload: TestingCoursePlaceholderPayload;
};

const imagesByTypeMap = {
    default: testPlaceholderSuccessPath,
    successNoCertificate: testPlaceholderSuccessPath,
    success: testPlaceholderSuccessPath,
    failure: testPlaceholderFailPath,
};

const getImage = (type: TestingCoursePlaceholderDataProps['type']) => {
    const imagePath = imagesByTypeMap[type];

    return <img src={imagePath} alt='testing-course-placeholder' />;
};

const getContent = (
    type: TestingCoursePlaceholderDataProps['type'],
    payload: TestingCoursePlaceholderPayload,
) => {
    const { t, test, course } = payload;

    switch (type) {
        case 'default': {
            return (
                <p>
                    {t('testing:blocks.titles.have_access_to_test_for_course')}
                    <br />
                </p>
            );
        }

        case 'successNoCertificate':
        case 'success': {
            const { resultScore, maxScore } = test.getScoresByType(course.test.showResultScore);
            const score = CommonHelper.createCountFormatter(
                resultScore,
                [
                    t('common:blocks.points.one'),
                    t('common:blocks.points.some'),
                    t('common:blocks.points.multiple'),
                ],
                { decimal: 2 },
            );

            return (
                <p>
                    {t('testing:blocks.titles.congratulation')}
                    <br />
                    {t('testing:blocks.titles.your_scores', { score, maxScore })}

                    <br />
                    <span
                        dangerouslySetInnerHTML={{
                            __html: t('testing:blocks.titles.your_course_usual_score', {
                                usualScore: test.usualScore,
                            }),
                        }}
                    />

                    <br />
                    <span
                        dangerouslySetInnerHTML={{
                            __html: t('testing:blocks.titles.your_course_final_assessment', {
                                score: test.finalAssessmentScore,
                            }),
                        }}
                    />
                </p>
            );
        }

        case 'failure': {
            return <p>{t('testing:blocks.titles.requirements_for_test_for_course')}</p>;
        }
    }
};

const getActions = (
    type: TestingCoursePlaceholderDataProps['type'],
    payload: TestingCoursePlaceholderPayload,
) => {
    const { t, course, isActiveTest, onStart } = payload;

    switch (type) {
        case 'default':
            return (
                <Button disabled={!course.hasQuestions()} onClick={onStart}>
                    {isActiveTest
                        ? t('testing:actions.continue_course_test')
                        : t('testing:actions.start_course_test')}
                </Button>
            );

        case 'success':
            return (
                <a rel='noopener noreferrer' target='_blank' href={course.certificateUrl}>
                    <Button>{t('testing:actions.download_certificate')}</Button>
                </a>
            );

        case 'successNoCertificate':
        case 'failure':
            return null;
    }
};

const getTestingCoursePlaceholderData = (
    type: TestingCoursePlaceholderDataProps['type'],
    payload: TestingCoursePlaceholderPayload,
) => {
    const result = {
        image: getImage(type),
        content: getContent(type, payload),
        actions: getActions(type, payload),
    };

    return result;
};

export { getTestingCoursePlaceholderData };
